import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaFlask, FaMicrochip, FaRobot, FaPuzzlePiece } from 'react-icons/fa';

const WorkshopsPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  // Workshop data
  const workshops = [
    {
      id: 1,
      title: 'STEMTronics Electronics Workshop',
      description: 'Specially designed for covering electronics part in school science syllabus. Students will learn about basic electronic components, circuits, and build their own electronic projects.',
      icon: <FaMicrochip className="text-5xl mb-4 text-blue-600" />,
      link: 'https://docs.google.com/forms/d/e/1FAIpQLSeXmT0PRTwJDwA_LsqLzzG1Y_HtTbnJvXS2h_byP7hkBB4UcQ/viewform'
    },
    {
      id: 2,
      title: 'School STEM Workshop',
      description: 'To inspire innovation through school children. This workshop introduces students to STEM concepts through hands-on activities and demonstrations.',
      icon: <FaFlask className="text-5xl mb-4 text-green-600" />,
      link: 'https://docs.google.com/forms/d/e/1FAIpQLSd0J5LDIzZnVGvzSi20-FFTQpkeRn-DL9qEO-Cx2TaMNnnK6g/viewform'
    },
    {
      id: 3,
      title: 'Fighting Robot Competition Workshop',
      description: 'This workshop is designed to develop the students programming skills. Learn how to build and program robots for competitive challenges.',
      icon: <FaRobot className="text-5xl mb-4 text-red-600" />,
      link: 'https://docs.google.com/forms/d/e/1FAIpQLScYwiG39kxW2fS-Cem154KUmeW44Vk0P9DK7ePdU3EkmxTWvA/viewform'
    },
    {
      id: 4,
      title: '3D Design & Printing Workshop',
      description: 'In this session, students can get more knowledge about 3D design & Printing. Learn about the latest additive manufacturing technologies and create your own 3D designs.',
      icon: <FaPuzzlePiece className="text-5xl mb-4 text-purple-600" />,
      link: 'https://docs.google.com/forms/d/e/1FAIpQLSfO9d_kutN4JmUzDokFCTUjsiBCBrVrj16oe_0-hwVsO80pNQ/viewform'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Workshops | STEM Labs</title>
        <meta name="description" content="Discover our STEM workshops designed to inspire innovation and creativity in students" />
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
            STEM Workshops
          </h1>
          <p className="text-white text-xl max-w-2xl mx-auto text-center">
            Hands-on learning experiences to inspire innovation and creativity in young minds
          </p>
        </div>
      </div>

      {/* Workshops Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">Our Workshop Programs</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Discover our range of workshops designed to provide hands-on learning experiences and develop critical STEM skills.
            </p>
          </div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {workshops.map((workshop) => (
              <motion.div 
                key={workshop.id}
                className="bg-white rounded-lg shadow-lg p-8 border border-gray-200 hover:border-blue-500 transition-all duration-300"
                variants={itemVariants}
              >
                <div className="flex flex-col items-center text-center">
                  {workshop.icon}
                  <h3 className="text-2xl font-bold mb-4">{workshop.title}</h3>
                  <p className="text-gray-600 mb-6">{workshop.description}</p>
                  <a 
                    href={workshop.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                  >
                    Register Now
                  </a>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Request Custom Workshop Section */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="md:flex">
              <div className="md:w-1/2 bg-blue-600 text-white p-8">
                <h3 className="text-2xl font-bold mb-4">Request a Custom Workshop</h3>
                <p className="mb-6">
                  Need a specialized workshop for your school, organization, or event? We can create a custom STEM experience tailored to your specific requirements.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
                    Flexible scheduling options
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
                    Tailored content for different age groups
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
                    Hands-on activities with all materials provided
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
                    Experienced STEM educators
                  </li>
                </ul>
              </div>
              <div className="md:w-1/2 p-8">
                <h3 className="text-2xl font-bold mb-4">Get in Touch</h3>
                <p className="text-gray-600 mb-6">
                  Fill out our workshop request form and our team will get back to you within 24 hours to discuss your specific needs.
                </p>
                <a 
                  href="https://docs.google.com/forms/d/e/1FAIpQLSfR7AKakDahctDOzwRNX6yHbGfTRzjo9Q669a9EpKkQPsE4VQ/viewform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full py-3 bg-blue-600 text-white text-center rounded hover:bg-blue-700 transition-colors"
                >
                  Request Workshop
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkshopsPage; 